import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Discount master (Masters → Discounts). Fixed rupee amount applied on the
 * itinerary Pricing step. `percent` is legacy (nullable) - existing rows keep
 * whatever percent they were saved with; new rows use `amountPaise`.
 */

/** Paise as a non-negative decimal string (BigInt-safe over JSON). */
const paiseString = z.string().regex(/^\d+$/, 'must be a non-negative integer paise string');

export const DiscountListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  /** Fixed discount amount in paise. */
  amountPaise: paiseString,
  /** Legacy percent off final markup (e.g. 5 = 5%). Null for fixed-amount rows. */
  percent: z.number().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type DiscountListItem = z.infer<typeof DiscountListItemSchema>;

export const DiscountListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type DiscountListQuery = z.infer<typeof DiscountListQuerySchema>;

export const DiscountListResponseSchema = paginatedResponseSchema(DiscountListItemSchema);
export type DiscountListResponse = z.infer<typeof DiscountListResponseSchema>;

export const DiscountDetailSchema = DiscountListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type DiscountDetail = z.infer<typeof DiscountDetailSchema>;

const DiscountCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  amountPaise: paiseString,
  /** Legacy percent field - not written by the current UI, kept for back-compat. */
  percent: z.number().min(0).max(100).nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateDiscountRequestSchema = DiscountCoreFieldsSchema;
export type CreateDiscountRequest = z.infer<typeof CreateDiscountRequestSchema>;

export const UpdateDiscountRequestSchema = DiscountCoreFieldsSchema.partial();
export type UpdateDiscountRequest = z.infer<typeof UpdateDiscountRequestSchema>;

export const DiscountRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  amountPaise: paiseString,
  percent: z.number().nullable(),
});
export type DiscountRef = z.infer<typeof DiscountRefSchema>;
